import { EventsService, PaginatedEventList } from 'api-clients/monolith';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Filterable } from 'components/FilterDropdown/util';

import { useLocalStorageItem } from '../../hooks/useLocalStorageItem';

export type EventQueryFunnel = {
  name: string;
  id: string;
};

export type EventQuery = {
  date: 'upcoming' | 'today' | 'past';
  stages: Filterable[];
  page: number;
  funnels: Filterable[];
  selectedColumns?: string[];
};

// Module-level cache per tab (date+stages+funnels), independent of page
type CachedTab = {
  response: PaginatedEventList;
  page: number;
  expiresAt: number;
  lastAccessed?: number;
};
const tabCache: Map<string, CachedTab> = new Map();
const LS_KEY = 'eventsTabCacheV1';
const MAX_LS_TABS = 12;

const buildTabKey = (
  date: EventQuery['date'],
  stageIds: string[],
  funnelIds: string[],
): string => `${date}|s:${stageIds.join(',')}|f:${funnelIds.join(',')}`;

const readCachedTab = (key: string): CachedTab | undefined => {
  if (typeof window === 'undefined') return undefined;
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    if (!raw) return undefined;
    const store = JSON.parse(raw) as Record<string, CachedTab>;
    const entry = store[key];
    if (!entry) return undefined;
    if (entry.expiresAt <= Date.now()) return undefined;
    return entry;
  } catch {
    return undefined;
  }
};

const writeCachedTab = (key: string, value: CachedTab): void => {
  if (typeof window === 'undefined') return;
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    const now = Date.now();
    const existing = raw ? (JSON.parse(raw) as Record<string, CachedTab>) : {};

    // Normalize (drop expired) and upsert via Map
    const map = new Map<string, CachedTab>(
      Object.entries(existing)
        .filter(
          ([, entry]) =>
            entry &&
            typeof entry.expiresAt === 'number' &&
            entry.expiresAt > now,
        )
        .map(([k, entry]) => [
          k,
          { ...entry, lastAccessed: entry.lastAccessed ?? 0 },
        ]),
    );
    map.set(key, { ...value, lastAccessed: now });

    // Sort by lastAccessed then expiresAt, keep most recent MAX_LS_TABS
    const finalEntries = Array.from(map.entries())
      .sort((a, b) => {
        const la = a[1].lastAccessed ?? 0;
        const lb = b[1].lastAccessed ?? 0;
        if (la !== lb) return la - lb;
        return (a[1].expiresAt || 0) - (b[1].expiresAt || 0);
      })
      .slice(-MAX_LS_TABS);

    const nextStore = finalEntries.reduce<Record<string, CachedTab>>(
      (acc, [k, entry]) => ({
        ...acc,
        [k]: entry,
      }),
      {},
    );

    window.localStorage.setItem(LS_KEY, JSON.stringify(nextStore));
  } catch {
    // ignore quota/parse errors
  }
};

export const useEvents = () => {
  const [filters, setFilters] = useLocalStorageItem<EventQuery>(
    {
      date: 'today',
      stages: [],
      page: 0,
      funnels: [],
    },
    'eventFilter',
  );
  const setFiltersRef = useRef(setFilters);
  useEffect(() => {
    setFiltersRef.current = setFilters;
  }, [setFilters]);
  const [data, setData] = useState<PaginatedEventList['events']>([]);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [lastResult, setLastResult] = useState<{
    status: 'loading' | 'ready' | 'error';
    isLoading: boolean;
    isError: boolean;
    data?: PaginatedEventList;
    error?: unknown;
  }>({
    status: 'loading',
    isLoading: true,
    isError: false,
  });
  const isFetching = useRef(false);

  const stageIds = useMemo(
    () => filters.stages.map(f => f.id).sort(),
    [filters.stages],
  );
  const funnelIds = useMemo(
    () => filters.funnels.map(f => f.id).sort(),
    [filters.funnels],
  );

  const prevTabKeyRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let currentRequest: ReturnType<
      typeof EventsService.getInternalApiEvents
    > | null = null;

    const fetchData = async () => {
      isFetching.current = true;

      const tabKey = buildTabKey(filters.date, stageIds, funnelIds);
      const cached = tabCache.get(tabKey) ?? readCachedTab(tabKey);
      const now = Date.now();
      const isTabSwitch = prevTabKeyRef.current !== tabKey;
      prevTabKeyRef.current = tabKey;

      if (cached && cached.expiresAt > now) {
        if (isTabSwitch || cached.page === filters.page) {
          setData(cached.response.events);
          setTotalPages(cached.response.pagination.total_pages);
          setLastResult({
            status: 'ready',
            data: cached.response,
            isLoading: false,
            isError: false,
          });
          isFetching.current = false;
          if (isTabSwitch && cached.page !== filters.page) {
            // Avoid infinite loops: only update if page changed
            const fn = setFiltersRef.current;
            if (fn) fn(prev => ({ ...prev, page: cached.page }));
          }
          return;
        }
        // Same tab but different page: show loader, keep current data to avoid flicker
        setLastResult({ status: 'loading', isLoading: true, isError: false });
      } else {
        // Tab switch with no cache: show loader and clear to avoid stale values
        setLastResult({ status: 'loading', isLoading: true, isError: false });
        setTotalPages(1);
        setData([]);
      }

      try {
        currentRequest = EventsService.getInternalApiEvents(
          filters.date,
          filters.page,
          stageIds,
          funnelIds,
        );

        const response = await currentRequest;

        if (!cancelled) {
          setData(response.events);
          setTotalPages(response.pagination.total_pages);
          // Cache latest fetched page for this tab (short TTL ~30s)
          const cachedTab: CachedTab = {
            response,
            page: filters.page,
            expiresAt: now + 30000,
          };
          tabCache.set(tabKey, cachedTab);
          writeCachedTab(tabKey, cachedTab);
          setLastResult({
            status: 'ready',
            data: response,
            isLoading: false,
            isError: false,
          });
        }
      } catch (error: unknown) {
        if (!cancelled) {
          setLastResult({
            status: 'error',
            isLoading: false,
            isError: true,
            error,
          });
        }
      } finally {
        if (!cancelled) {
          isFetching.current = false;
        }
      }
    };

    void fetchData();

    return () => {
      cancelled = true;
      if (currentRequest && typeof currentRequest.cancel === 'function') {
        currentRequest.cancel();
      }
      isFetching.current = false;
    };
  }, [filters.date, filters.page, stageIds, funnelIds]);

  const refetch = useCallback(() => {
    if (isFetching.current) {
      return;
    }

    isFetching.current = true;
    setLastResult({ status: 'loading', isLoading: true, isError: false });

    void EventsService.getInternalApiEvents(
      filters.date,
      filters.page,
      stageIds,
      funnelIds,
    )
      .then(response => {
        setData(response.events);
        setTotalPages(response.pagination.total_pages);
        setLastResult({
          status: 'ready',
          data: response,
          isLoading: false,
          isError: false,
        });
      })
      .catch((error: unknown) => {
        setLastResult({
          status: 'error',
          isLoading: false,
          isError: true,
          error,
        });
      })
      .finally(() => {
        isFetching.current = false;
      });
  }, [filters.date, filters.page, stageIds, funnelIds]);

  const next = useCallback(() => {}, []);

  return {
    filters,
    setFilters,
    data,
    lastResult,
    next,
    refetch,
    totalPages,
  };
};
